"use client";
import { useScroll, useTransform, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import WorkAvatar from '../assets/workAvatar.png';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
export const Timeline = ({ data }) => {
  const ref = useRef(null);
  const containerRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!ref.current) return;

    const updateHeight = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setHeight(rect.height);
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
      // Force refresh ScrollTrigger and Framer Motion calculations
      ScrollTrigger.refresh();
      window.dispatchEvent(new Event('resize'));
    });

    resizeObserver.observe(ref.current);
    updateHeight(); // Initial call

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    // Only refresh ScrollTrigger when height actually changes to avoid loops
    ScrollTrigger.refresh();
  }, [height]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end center"],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  const animationProps = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: false, amount: 0.9 },
    transition: { duration: 1.5, delay: 0.01 }
  };

  // GSAP Animations for Work Experience title
  useEffect(() => {
    // Animate the work avatar image
    gsap.fromTo(
      '.work-avatar-img',
      {
        opacity: 0,
        scale: 0,
        rotation: -180,
      },
      {
        opacity: 1,
        scale: 1,
        rotation: 0,
        duration: 1.2,
        ease: 'elastic.out(1, 0.5)',
        scrollTrigger: {
          trigger: '.work-title-container',
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );

    // Animate the "un" text in "Experience"
    gsap.fromTo(
      '.experience-un',
      {
        opacity: 0,
        y: 50,
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        delay: 0.3,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.work-title-container',
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);



  return (
    <div
      className="w-full font-sans lg:pt-16 pt-10 md:px-10"
      ref={containerRef}
    >
      <div className="md:max-w-7xl w-full mx-auto pt-20 pb-12 md:px-8 lg:px-10">
        <div className="flex flex-col items-center text-center relative overflow-visible">
          {/* Decorative Sub-header */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-4 mb-6"
          >
            <div className="h-px w-12 md:w-24 bg-gradient-to-r from-transparent via-blue-500/40 to-blue-500/60" />
            <span className="text-xs md:text-sm font-bold tracking-[0.4em] uppercase text-blue-600 dark:text-blue-400">My Career Journey</span>
            <div className="h-px w-12 md:w-24 bg-gradient-to-l from-transparent via-blue-500/40 to-blue-500/60" />
          </motion.div>

          <motion.h2
            {...animationProps}
            className="work-title-container text-zinc-900 dark:text-zinc-100 text-6xl md:text-6xl lg:text-8xl font-black tracking-tighter uppercase leading-[0.85] relative z-20 select-none pb-2"
          >
            <div className="flex flex-row justify-center items-center">W <img src={WorkAvatar} alt="" className="work-avatar-img lg:w-32 w-28" /> rk</div>
            {/* <br className="hidden md:block" /> */}
            <span className="bg-clip-text -mt-10 text-transparent bg-gradient-to-r from-zinc-400 !capitalize  to-zinc-400 dark:from-zinc-600  dark:to-zinc-600">Experience{' '}</span>
          </motion.h2>

          {/* Background Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[600px] md:h-[400px] bg-blue-500/5 dark:bg-blue-600/10 blur-[100px] md:blur-[150px] rounded-full -z-10 pointer-events-none" />
        </div>
      </div>

      <div ref={ref} className="relative w-full md:max-w-7xl mx-auto pb-14 md:pb-20">
        {/* Timeline Line - Behind everything */}
        <div
          style={{
            height: height + "px",
          }}
          className="absolute left-8 md:left-[33.33%] top-0 w-[2px] bg-gradient-to-b from-transparent via-neutral-200 dark:via-neutral-700 to-transparent hidden md:block"
        >
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="absolute inset-x-0 top-0 w-[2px] bg-gradient-to-t from-purple-500 via-blue-500 to-transparent rounded-full shadow-[0_0_10px_rgba(147,51,234,0.5)]"
          />
        </div>

        {data.map((item, index) => (
          <div key={index} className="relative py-12 md:py-20 lg:py-32">
            <div className="flex flex-col md:flex-row md:gap-0">
              {/* Mobile Year Label */}
              <div className="block md:hidden mb-6 pl-8">
                <h3 className="text-4xl font-bold text-zinc-400 dark:text-zinc-600">
                  {item.title}
                </h3>
              </div>

              {/* Left Section - Year (Sticky - Desktop only) */}
              <div className="hidden md:block md:w-[33.33%] self-start">
                <div className="sticky top-24">
                  <h3 className="dark:stroke stroke-light text-7xl lg:text-9xl border-zinc-900 font-bold hover:text-zinc-500/50 dark:hover:text-zinc-100 transition-all duration-500 text-right pr-12 select-none tracking-tighter opacity-20">
                    {item.title}
                  </h3>
                </div>
              </div>

              {/* Mobile Timeline Dot - Left side */}
              <div className="block md:hidden absolute left-0 top-12 z-20">
                <div className="h-4 w-4 rounded-full bg-white dark:bg-black flex items-center justify-center border-2 border-neutral-300 dark:border-neutral-600 shadow-lg">
                  <div className="h-2 w-2 rounded-full bg-neutral-400 dark:bg-neutral-500"></div>
                </div>
              </div>

              {/* Timeline Dot - Absolutely positioned on the line (Desktop) */}
              <div className="hidden md:block absolute left-[33.33%] top-8 -translate-x-1/2 z-20">
                <div className="relative">
                  <div className="h-5 w-5 rounded-full bg-white mt-32 dark:bg-black flex items-center justify-center border-2 border-neutral-300 dark:border-neutral-600 shadow-lg">
                    <motion.div
                      className="h-2.5 w-2.5 rounded-full bg-neutral-400 dark:bg-neutral-500"
                      style={{
                        scaleY: scrollYProgress,
                        transformOrigin: "top",
                      }}
                    />
                  </div>
                  {/* Glow effect when line reaches */}
                  <motion.div
                    style={{
                      opacity: opacityTransform,
                    }}
                    className="absolute inset-0 rounded-full bg-blue-500/30 blur-md"
                  />
                </div>
              </div>

              {/* Right Section - Content */}
              <div className="w-full md:w-[66.67%] pl-8 md:pl-12">
                {item.content}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile Vertical Line - Left side */}
      <div
        className="block md:hidden absolute left-[7px] top-0 w-[2px] bg-gradient-to-b from-transparent via-neutral-300 dark:via-neutral-700 to-transparent"
        style={{ height: `${height}px` }}
      />
    </div>
  );
};
